/**
 * frameStore — IndexedDB persistence for captured scan frames.
 *
 * Survives browser kills, phone calls, and tab crashes.
 * Frames are persisted immediately on capture and cleaned up after successful upload.
 *
 * Database: dukkan-frames (v1)
 * Object stores:
 *   - pending-frames: individual frame blobs keyed by {scanSessionId}_{frameIndex}
 *   - scan-sessions:  scan metadata (shopId, sectionId, progress, SAS URLs)
 */

// ── Types ──

export interface PersistedFrame {
  /** Composite key: `${scanSessionId}_${frameIndex}` */
  id: string
  scanSessionId: string
  frameIndex: number
  blob: Blob
  hdBlob: Blob
  /** 'pending' = not yet uploaded, 'uploaded' = done, 'failed' = upload error */
  status: 'pending' | 'uploaded' | 'failed'
  createdAt: number
}

export interface ScanSession {
  /** Unique ID generated at capture start (NOT the server scan ID — that comes later) */
  id: string
  /** Server-side scan ID (set after createScan API call) */
  serverScanId?: string
  shopId: string
  sectionId: string
  sectionName: string
  frameCount: number
  /** How many frames have been uploaded successfully */
  uploadedCount: number
  /** 'capturing' | 'ready' | 'uploading' | 'completed' | 'failed' */
  status: 'capturing' | 'ready' | 'uploading' | 'completed' | 'failed'
  location?: { lat: number; lng: number } | null
  createdAt: number
  updatedAt: number
}

// ── Constants ──

const DB_NAME = 'dukkan-frames'
const DB_VERSION = 1
const FRAMES_STORE = 'pending-frames'
const SESSIONS_STORE = 'scan-sessions'

// ── Database ──

let dbPromise: Promise<IDBDatabase> | null = null

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result

      // Frames store
      if (!db.objectStoreNames.contains(FRAMES_STORE)) {
        const frameStore = db.createObjectStore(FRAMES_STORE, { keyPath: 'id' })
        frameStore.createIndex('scanSessionId', 'scanSessionId', { unique: false })
        frameStore.createIndex('status', 'status', { unique: false })
      }

      // Sessions store
      if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
        db.createObjectStore(SESSIONS_STORE, { keyPath: 'id' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      console.error('[frameStore] Failed to open IndexedDB:', request.error)
      reject(request.error)
    }
  })

  return dbPromise
}

// ── Helper: wrap IDBRequest as Promise ──

function reqToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function txToPromise(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    tx.onabort = () => reject(tx.error)
  })
}

// ── Session Operations ──

export async function createSession(session: ScanSession): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(SESSIONS_STORE, 'readwrite')
  tx.objectStore(SESSIONS_STORE).put(session)
  await txToPromise(tx)
}

export async function getSession(sessionId: string): Promise<ScanSession | undefined> {
  const db = await openDB()
  const tx = db.transaction(SESSIONS_STORE, 'readonly')
  return reqToPromise(tx.objectStore(SESSIONS_STORE).get(sessionId))
}

export async function updateSession(sessionId: string, updates: Partial<ScanSession>): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(SESSIONS_STORE, 'readwrite')
  const store = tx.objectStore(SESSIONS_STORE)
  const existing = await reqToPromise(store.get(sessionId))
  if (existing) {
    Object.assign(existing, updates, { updatedAt: Date.now() })
    store.put(existing)
  }
  await txToPromise(tx)
}

export async function deleteSession(sessionId: string): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(SESSIONS_STORE, 'readwrite')
  tx.objectStore(SESSIONS_STORE).delete(sessionId)
  await txToPromise(tx)
}

/**
 * Get all sessions that can be resumed (status = 'capturing' | 'ready' | 'uploading').
 */
export async function getPendingSessions(): Promise<ScanSession[]> {
  const db = await openDB()
  const tx = db.transaction(SESSIONS_STORE, 'readonly')
  const all: ScanSession[] = await reqToPromise(tx.objectStore(SESSIONS_STORE).getAll())
  return all.filter((s) => s.status === 'capturing' || s.status === 'ready' || s.status === 'uploading')
}

// ── Frame Operations ──

export async function saveFrame(frame: PersistedFrame): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(FRAMES_STORE, 'readwrite')
  tx.objectStore(FRAMES_STORE).put(frame)
  await txToPromise(tx)
}

export async function getFramesBySession(scanSessionId: string): Promise<PersistedFrame[]> {
  const db = await openDB()
  const tx = db.transaction(FRAMES_STORE, 'readonly')
  const index = tx.objectStore(FRAMES_STORE).index('scanSessionId')
  return reqToPromise(index.getAll(scanSessionId))
}

export async function getPendingFrames(scanSessionId: string): Promise<PersistedFrame[]> {
  const frames = await getFramesBySession(scanSessionId)
  return frames.filter((f) => f.status === 'pending')
}

export async function markFrameUploaded(frameId: string): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(FRAMES_STORE, 'readwrite')
  const store = tx.objectStore(FRAMES_STORE)
  const frame = await reqToPromise(store.get(frameId))
  if (frame) {
    frame.status = 'uploaded'
    store.put(frame)
  }
  await txToPromise(tx)
}

export async function markFrameFailed(frameId: string): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(FRAMES_STORE, 'readwrite')
  const store = tx.objectStore(FRAMES_STORE)
  const frame = await reqToPromise(store.get(frameId))
  if (frame) {
    frame.status = 'failed'
    store.put(frame)
  }
  await txToPromise(tx)
}

export async function deleteFrame(frameId: string): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(FRAMES_STORE, 'readwrite')
  tx.objectStore(FRAMES_STORE).delete(frameId)
  await txToPromise(tx)
}

/**
 * Delete all frames and the session for a given scan session.
 */
export async function deleteSessionAndFrames(scanSessionId: string): Promise<void> {
  const db = await openDB()
  const tx = db.transaction([FRAMES_STORE, SESSIONS_STORE], 'readwrite')

  // Delete all frames
  const frameStore = tx.objectStore(FRAMES_STORE)
  const index = frameStore.index('scanSessionId')
  const frames: PersistedFrame[] = await reqToPromise(index.getAll(scanSessionId))
  for (const f of frames) {
    frameStore.delete(f.id)
  }

  // Delete session
  tx.objectStore(SESSIONS_STORE).delete(scanSessionId)

  await txToPromise(tx)
}

/**
 * Estimate IndexedDB storage usage (if StorageManager available).
 * Returns { usage, quota } in bytes, or null if not available.
 */
export async function getStorageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (navigator.storage && navigator.storage.estimate) {
    const est = await navigator.storage.estimate()
    return { usage: est.usage || 0, quota: est.quota || 0 }
  }
  return null
}

/**
 * Generate a unique scan session ID (client-side, before server-side scan creation).
 */
export function generateSessionId(): string {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}
